import React, { useState } from 'react';
import { Phone, Mail, MessageCircle, Facebook, Instagram, MapPin, Clock, Send } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { contactInfo, appointmentTypes } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = ({ onChatOpen }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    appointmentType: '',
    preferredDate: '',
    message: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({
      ...prev,
      appointmentType: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Send to backend API
      const response = await axios.post(`${API}/appointments`, formData);
      
      console.log('Appointment saved to backend:', response.data);
      
      toast.success('Appointment request submitted! We will contact you soon.', {
        description: `Our team will reach out to you at ${formData.phone} within 24 hours.`,
      });

      // Try to send WhatsApp notification to admin (non-blocking)
      try {
        const adminWhatsApp = '9425025425';
        const adminMessage = `🔔 NEW CONSULTATION REQUEST

📋 Patient: ${formData.name}
📞 Phone: ${formData.phone}
📧 Email: ${formData.email || 'Not provided'}
🏥 Type: ${formData.appointmentType}
📅 Preferred Date: ${formData.preferredDate || 'Not specified'}
💬 Message: ${formData.message || 'No additional message'}`;

        // Open WhatsApp to admin number with notification
        window.open(`https://wa.me/91${adminWhatsApp}?text=${encodeURIComponent(adminMessage)}`, '_blank');
      } catch (whatsappError) {
        // WhatsApp notification failed but appointment is already saved
        console.log('WhatsApp notification could not be sent, but appointment is saved');
      }

      // Reset form
      setFormData({
        name: '',
        phone: '',
        email: '',
        appointmentType: '',
        preferredDate: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting appointment:', error);
      toast.error('Failed to submit appointment', {
        description: 'Please try again or call us directly at 9244093291.'
      });
    }
  };

  return (
    <div className="contact-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">Get in Touch</h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              Reach out to us for consultation, guidance, or to schedule an appointment. 
              We're here to support you every step of the way.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto -mt-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="bg-blue-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="text-blue-600" size={24} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Phone</h3>
                <a href={`tel:${contactInfo.phone}`} className="text-blue-600 hover:text-blue-700 font-medium">
                  {contactInfo.phone}
                </a>
                <p className="text-sm text-gray-600 mt-2">24/7 Support Available</p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all">
              <CardContent className="p-6 text-center">
                <div className="bg-green-100 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MessageCircle className="text-green-600" size={24} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">WhatsApp</h3>
                <a 
                  href={`https://wa.me/${contactInfo.whatsapp}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 hover:text-green-700 font-medium"
                >
                  Chat With Us
                </a>
                <p className="text-sm text-gray-600 mt-2">Quick Response</p>
              </CardContent>
            </Card>

            <Card 
              className="border-none shadow-lg hover:shadow-xl transition-all cursor-pointer hover:scale-105 hover:border-4 hover:border-blue-500 ring-2 ring-blue-300 animate-pulse"
              onClick={onChatOpen}
            >
              <CardContent className="p-6 text-center">
                <div className="bg-blue-600 w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 animate-bounce shadow-lg shadow-blue-300">
                  <Clock className="text-white" size={24} />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Availability</h3>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    onChatOpen();
                  }}
                  className="text-3xl font-bold text-blue-600 hover:text-blue-700 transition-colors cursor-pointer"
                >
                  24/7
                </button>
                <div className="mt-2">
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold animate-pulse">
                    🟢 LIVE CHAT
                  </span>
                </div>
                <p className="text-sm text-blue-600 font-bold mt-2 animate-bounce">👆 Click to Chat Now!</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form & Social */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
            {/* Form */}
            <div className="lg:col-span-2">
              <Card className="border-none shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-gray-900 mb-6">Book a Consultation</h2>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          placeholder="Your name"
                          required
                          className="border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="phone">Phone Number *</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="Your phone number"
                          required
                          className="border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="your.email@example.com"
                        className="border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="appointmentType">Consultation Type *</Label>
                        <Select onValueChange={handleSelectChange} value={formData.appointmentType} required>
                          <SelectTrigger className="border-blue-200 focus:border-blue-500 focus:ring-blue-500">
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {appointmentTypes.map((type) => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="preferredDate">Preferred Date</Label>
                        <Input
                          id="preferredDate"
                          name="preferredDate"
                          type="date"
                          value={formData.preferredDate}
                          onChange={handleChange}
                          min={new Date().toISOString().split('T')[0]}
                          className="border-blue-200 focus:border-blue-500 focus:ring-blue-500"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Message</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Tell us about your situation or any specific concerns..."
                        rows={5}
                        className="border-blue-200 focus:border-blue-500 focus:ring-blue-500 resize-none"
                      />
                    </div>

                    <Button 
                      type="submit" 
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg rounded-full shadow-lg hover:shadow-xl transition-all"
                    >
                      Submit Request
                      <Send size={18} className="ml-2" />
                    </Button>

                    <p className="text-sm text-gray-600 text-center">
                      We'll contact you within 24 hours to confirm your appointment
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Social Media */}
              <Card className="border-none shadow-xl">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Connect Online</h3>
                  <div className="space-y-4">
                    <a 
                      href={`https://wa.me/${contactInfo.whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-green-50 hover:bg-green-100 rounded-lg transition-all group"
                    >
                      <div className="bg-green-600 p-2 rounded-lg group-hover:scale-110 transition-transform">
                        <MessageCircle size={20} className="text-white" />
                      </div>
                      <span className="font-medium text-gray-900">WhatsApp</span>
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* Emergency */}
              <Card className="bg-gradient-to-br from-blue-600 to-blue-500 text-white border-none shadow-xl">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold mb-3">Need Urgent Care?</h3>
                  <p className="text-blue-50 mb-4 text-sm">
                    For urgent pain management or immediate support, please call us directly.
                  </p>
                  <a href={`tel:${contactInfo.phone}`}>
                    <Button className="w-full bg-white text-blue-600 hover:bg-blue-50 font-semibold">
                      <Phone size={18} className="mr-2" />
                      Call Now: {contactInfo.phone}
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
